import type { ActionButtonsProps } from '@app/src/components/ActionButtons'
import ActionButtons from '@app/src/components/ActionButtons'
import type { TabsProps } from '@app/src/components/Tabs'
import Tabs from '@app/src/components/Tabs'
import { Box, Stack, Typography } from '@mui/material'
import type { PropsWithChildren, ReactNode } from 'react'
import React, { useMemo } from 'react'
import { Helmet } from 'react-helmet'
import { useHistory } from 'react-router'

export type ResourceCollectionSceneProps = {
  title: ReactNode
  subtitle?: ReactNode
  tabs?: {
    actionTabs: TabsProps['tabs']
    activeTabParam: string
    onTabChange?: (value: string) => void
  }
  showTabsBottomBorder?: boolean
  contentPrefix?: ReactNode
  filter?: ReactNode
  actionButtons?: ActionButtonsProps['buttons']
  buttonRow?: ActionButtonsProps['buttons']
  buttonRowPrefix?: ReactNode
  embedded?: boolean
  enableScroll?: boolean
  disableLeftPadding?: boolean
  allowOverflow?: boolean
}

const ResourceCollectionScene: React.FC<PropsWithChildren<ResourceCollectionSceneProps>> = ({
  title,
  subtitle,
  tabs,
  showTabsBottomBorder = false,
  actionButtons = [],
  contentPrefix,
  filter,
  buttonRow,
  buttonRowPrefix,
  enableScroll,
  embedded,
  disableLeftPadding,
  children,
  allowOverflow,
}) => {
  const history = useHistory()

  const handleTabChange = (value: string): void => {
    const selectedTab = tabs?.actionTabs.find(t => t.type === value)

    if (selectedTab) {
      history.push(selectedTab.url)
    }
  }

  const helmetTitle = useMemo(() => {
    if (typeof title !== 'string' && !(title instanceof String)) return undefined
    const activeTabLabel = tabs?.actionTabs.find(t => t.type === tabs?.activeTabParam)?.label
    return activeTabLabel ? `${title} - ${activeTabLabel}` : title.toString()
  }, [title, tabs?.actionTabs, tabs?.activeTabParam])

  return (
    <Box
      flexGrow={1}
      display="flex"
      flexDirection="column"
      overflow={allowOverflow ? undefined : 'hidden'}
      height="100%"
      data-testid="resource-collection"
      sx={allowOverflow ? { scrollbarGutter: 'stable' } : undefined}
    >
      {helmetTitle && (
        <Helmet>
          <title>{helmetTitle}</title>
        </Helmet>
      )}

      <Box px={embedded ? 0 : 4} pt={embedded ? 0 : 3}>
        {!embedded && (
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h1" data-testid="resource-collection-title">
              {title}
            </Typography>
            <Stack direction="row" spacing={1}>
              {actionButtons && <ActionButtons buttons={actionButtons} />}
            </Stack>
          </Box>
        )}
        {subtitle && <Box mt={embedded ? 0 : 2}>{subtitle}</Box>}

        {tabs?.actionTabs && (
          <Tabs
            activeTab={tabs?.activeTabParam}
            tabs={tabs?.actionTabs}
            handleChange={(_, value) => (tabs.onTabChange ? tabs.onTabChange(value) : handleTabChange(value))}
            showBottomBorder={showTabsBottomBorder}
            sx={{ pt: 2 }}
          />
        )}
      </Box>
      {contentPrefix}
      <Box
        mt={!tabs && !embedded ? 3 : 0}
        flexGrow={1}
        display="flex"
        flexDirection="column"
        overflow={allowOverflow ? undefined : 'hidden'}
      >
        {(filter || buttonRow || buttonRowPrefix) && (
          <Stack py={3} pl={embedded ? 0 : 4} pr={4} gap={2} bgcolor="grey.200">
            {filter && <Box>{filter}</Box>}
            {(buttonRow || buttonRowPrefix) && (
              <Stack direction="row" gap={1}>
                {buttonRowPrefix}
                {buttonRow && <ActionButtons sizes="small" variants="outlined" buttons={buttonRow} />}
              </Stack>
            )}
          </Stack>
        )}

        <Box
          display="flex"
          flexDirection="column"
          flexGrow={1}
          height={enableScroll ? 'auto' : 10}
          pl={disableLeftPadding || embedded ? 0 : 4}
          bgcolor="grey.100"
        >
          {children}
        </Box>
      </Box>
    </Box>
  )
}

export default ResourceCollectionScene
